import { Button } from "@mui/material";
import { useMemo, useState } from "react";
import { BackText, ReplacementReviewSubmitOrder } from "constants/text";
import { v4 as uuidv4 } from "uuid";
import { useCreateReplacementOrderMutation } from "features/api/orderApi.ts";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { cartActions, ReplacementCartItem } from "features/reducers/replacementOrder/cart.ts";
import { submittingReplacementOrderActions } from "features/reducers/replacementOrder/submittingReplacementOrder.ts";
import { submittedReplacementOrdersActions } from "features/reducers/replacementOrder/submittedReplacementOrders.ts";
import { trackGA4Event } from "utils/googleAnalytics";
import { APIErrorResponse } from "features/api/utils/apiUtils.ts";
import { models } from "types/api/viewModels.ts";
import { PostReplacementOrderRequestBody } from "types/api/orders/postReplacementOrder.ts";
import useBlobStorage from "hooks/useBlobStorage.ts";
import { uploadFileToBlob } from "utils/blob";
import { ReplacementReviewButtonsWrapper } from "./ReplacementReviewButtons.styles.ts";
import { OrderSubmissionAvailability, useOrderSubmissionState } from "features/Messages/OrderSubmissionAvailability";

export interface ReplacementsReviewButtons {
	orderDetail: models["OrderDetailViewModel"] | null | undefined;
	accounts: models["CustomerAccountViewModel"] | undefined;
	handleReplacementError: (error: APIErrorResponse["errors"]) => void;
}

const ReplacementReviewButtons = ({ orderDetail, accounts, handleReplacementError }: ReplacementsReviewButtons) => {
	const [createReplacementOrder] = useCreateReplacementOrderMutation();
	const cartItems: ReplacementCartItem[] = useSelector((state: RootState) => state.cart.replacements);
	const details = useSelector((state: RootState) => state.submittingReplacementOrder.order);
	const billToId = details?.billingDetails?.billToId;
	const navigateTo = useNavigate();
	const { uploadContainerBlob } = useBlobStorage();
	const dispatch = useDispatch();
	const [disabled, setDisabled] = useState(false);
	const { orderBlocked, updateAvailability } = useOrderSubmissionState();

	const replaceableParts = useMemo(() => cartItems.map((item: any) => item.replaceableParts), [cartItems]).flat();

	const wholeCabinetOrders = cartItems.reduce((accum: any, item: any) => {
		if (item.wholeCabinetQuantity > 0) {
			accum.push({
				originalLineItemId: item.lineItemId,
				id: item.id,
				hasAttachments: item.cabinetFileUploads && item.cabinetFileUploads.length > 0,
				defectCode: item.cabinetDefectCode,
				quantityOrdered: item.wholeCabinetQuantity,
				comments: item.comment,
				qualityCheckFlag: item.qualityCheckFlag ?? false
			});
		}
		return accum;
	}, []);

	const cabinetPartOrders = replaceableParts.reduce((accum: any, part: any) => {
		if (part.partQuantity > 0) {
			accum.push({
				originalLineItemId: part.lineItemId,
				id: part.id,
				hasAttachments: part.partFileUploads && part.partFileUploads.length > 0,
				defectCode: part.partDefectCode,
				quantityOrdered: part.partQuantity,
				comments: part.comment,
				qualityCheckFlag: part.qualityCheckFlag ?? false
			});
		}
		return accum;
	}, []);

	const combinedOrders =
		cabinetPartOrders?.length > 0 ? wholeCabinetOrders.concat(cabinetPartOrders) : wholeCabinetOrders;

	const shipToAddresses = useMemo(
		() => accounts?.billTos?.flatMap((address: models["BillToViewModel"]) => address.shipTos),
		[accounts]
	);

	const selectedAddress = shipToAddresses?.reduce((accum: any, address: any) => {
		if (address.isSelected) {
			accum.push({
				shipToId: address.shipToId
			});
		}
		return accum;
	}, []);

	const uploadToBlobStorage = async (uuID: string, newLineItems: any[]): Promise<string[]> => {
		const uploadedBlobs: string[] = [];

		// Loop through each cart item to get the uploads and lineItemNumber, to be able to upload each image from the cart
		for (const cartItem of cartItems) {
			const { cabinetFileUploads, replaceableParts, cabinetDefectCode } = cartItem;
			const partUploads = replaceableParts.flatMap((part) => part.partFileUploads);
			const combinedArray = [...cabinetFileUploads, ...partUploads];
			if (partUploads.length > 0) {
				Array.from(replaceableParts.filter((p) => p.partQuantity > 0)).forEach((part) => {
					trackGA4Event({
						event: "Replacement - Submitted Images",
						eventCategory: "replacements",
						eventLabel: "replacement images uploaded",
						eventAction: "submit",
						replacementUploadType: "part",
						numberOfFileUploads: part.partFileUploads.length,
						defectCode: part.partDefectCode
					});
				});
			}

			if (wholeCabinetOrders.length > 0 && cabinetDefectCode && cabinetFileUploads.length > 0) {
				trackGA4Event({
					event: "Replacement - Submitted Images",
					eventCategory: "replacements",
					eventLabel: "replacement images uploaded",
					eventAction: "submit",
					replacementUploadType: "whole cabinet",
					numberOfFileUploads: cabinetFileUploads.length,
					defectCode: cabinetDefectCode
				});
			}
			for (const file of combinedArray) {
				try {
					const { fileUrl, size, name, id, originalLineItemId } = file;
					const newLineItemNumber =
						newLineItems.findIndex(
							(lineItem) => lineItem.id === id && lineItem.originalLineItemId === originalLineItemId
						) + 1;
					const fileName = `${newLineItemNumber}/${name}`;
					await uploadFileToBlob({
						fileUrl,
						fileName,
						size,
						requestId: uuID,
						uploadContainerBlob
					});
					uploadedBlobs.push(id);
				} catch (e) {
					dispatch(cartActions.setErrorState());
					console.error(e);
				}
			}
		}

		return uploadedBlobs;
	};

	const calculateShipToAddress = () => {
		// When shipping details exists and shipping details id exists assign id
		// Otherwise the calculateShipToId should remain undefined
		// Get shipToSelection from shippingDetails when applicable otherwise select it from billingDetails using default shipToId

		const calculateShipToId = details?.shippingDetails
			? details?.shippingDetails?.shipToId
			: selectedAddress[0]?.shipToId;
		const shipToSelection =
			details?.shippingDetails ?? details?.billingDetails?.shipTos?.find((x) => x.shipToId === calculateShipToId);
		const hasPhoneNumber = Boolean(
			details?.shippingDetails?.address?.phoneNumber &&
				details?.shippingDetails?.address.phoneNumber.trim() !== ""
		);
		const hasEmail = Boolean(
			details?.shippingDetails?.address?.email && details?.shippingDetails?.address.email.trim() !== ""
		);
		return {
			shipToId: calculateShipToId,
			shipToAddress: {
				phoneNumber: hasPhoneNumber ? shipToSelection?.address?.phoneNumber : undefined,
				email: hasEmail ? shipToSelection?.address?.email : undefined,
				...(!details?.isCustomerPickup && {
					name: shipToSelection?.address?.name,
					line1: shipToSelection?.address?.line1,
					line2: shipToSelection?.address?.line2,
					line3: shipToSelection?.address?.line3,
					city: shipToSelection?.address?.city,
					state: shipToSelection?.address?.state,
					zip: shipToSelection?.address?.zip,
					county: shipToSelection?.address?.county ?? ""
				})
			}
		};
	};

	const submitReplacement = async () => {
		const uuID: string = uuidv4();
		setDisabled((prevState) => !prevState);

		const { shipToId, shipToAddress } = calculateShipToAddress();

		// Populate base fields used for replacement order
		let replacementOrder: PostReplacementOrderRequestBody = {
			tandemOrderId: uuID,
			originalOrderId: orderDetail?.orderId,
			lineItems: combinedOrders,
			accountId: accounts?.accountId,
			jobName: details?.jobName ?? orderDetail?.jobName,
			poNumber: details?.poNumber ?? orderDetail?.poNumber,
			billToId: String(billToId),
			isCustomerPickup: details?.isCustomerPickup ?? orderDetail?.shippingDetails?.shipVia === "CUSTOMER_PICKUP",
			labelComments: details?.labelComments,
			shipToId: details?.isCustomerPickup ? undefined : shipToId,
			shipToAddress: shipToAddress
		};

		if (replacementOrder.tandemOrderId && replacementOrder.lineItems) {
			const successfulUploads = await uploadToBlobStorage(
				replacementOrder.tandemOrderId,
				replacementOrder.lineItems
			);
			replacementOrder.lineItems.forEach((lineItem) => {
				lineItem.hasAttachments = lineItem.id ? successfulUploads.includes(lineItem.id) : false;
			});
		}
		try {
			// Encode the replacementOrder object fields to Base64
			const confirmationNumber = await createReplacementOrder(replacementOrder).unwrap();
			if (typeof confirmationNumber === "string" && confirmationNumber.trim() !== "") {
				dispatch(submittingReplacementOrderActions.updateConfirmationNumber(confirmationNumber));
			}
			dispatch(submittedReplacementOrdersActions.updateOrders(replacementOrder?.originalOrderId));
			navigateTo(`/replacements/confirmation/${orderDetail?.orderId}`);
		} catch (error: any) {
			if (error instanceof DOMException && error.name === "InvalidCharacterError") {
				console.error("Invalid character in string for btoa encoding:", error);
			}

			// Handle API call errors based on status code
			switch (error?.data?.status) {
				case 403:
					return navigateTo("/unapproved/account");
				case 404:
					return navigateTo("/ordernotfound");
				case 409:
					return navigateTo(`/replacements/resubmit/${orderDetail?.orderId}`);
				case 400:
					setTimeout(() => window.scrollTo(0, 0), 0);
					handleReplacementError(error.data.errors);
					break;
				default:
					console.error("An unexpected error occurred:", error);
			}
		}
	};

	const handleBackNavigation = () => {
		navigateTo(`/replacements/shipping-details/${orderDetail?.orderId}`);
	};

	return (
		<>
			<OrderSubmissionAvailability
				targets={(orderDetail?.configurations ?? [])
					.filter((configuration) =>
						configuration.lineItems?.some((line) =>
							combinedOrders.some(
								(item: { originalLineItemId?: string }) => item.originalLineItemId === line.lineItemId
							)
						)
					)
					.map((configuration) => ({
						accountId: accounts?.accountId ?? "",
						productLineId: configuration.globals?.productLine?.id ?? ""
					}))}
				onChange={updateAvailability}
			/>
			<ReplacementReviewButtonsWrapper>
				<Button
					variant="text"
					fullWidth
					data-testid="replacementReason-backButton"
					onClick={handleBackNavigation}
				>
					{BackText.toUpperCase()}
				</Button>
				<Button
					fullWidth
					onClick={submitReplacement}
					disabled={disabled || orderBlocked}
					variant="contained"
					data-testid="replacementReview-submitOrder"
					data-id="replacementReview-submitOrder"
				>
					{ReplacementReviewSubmitOrder}
				</Button>
			</ReplacementReviewButtonsWrapper>
		</>
	);
};

export default ReplacementReviewButtons;
